import browser from 'Cloud/browser';
import React, { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getIsCloudAvailabe } from 'reactApp/appHelpers/appHelpers';
import { IS_B2B_BIZ_USER, IS_BIZ_USER, IS_CORP_USER, IS_TABLET_BROWSER, IS_TARM } from 'reactApp/appHelpers/configHelpers';
import { tooltipLimitPublic } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { type AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { isEditPublicCopy } from 'reactApp/modules/features/features.helpers';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { getCurrentItem } from 'reactApp/modules/storage/storage.selectors';
import { type CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { EditorRestrictionTooltipWrapper } from 'reactApp/ui/EditorRestriction/EditorRestrictionTooltip/EditorRestrictionTooltipWrapper';
import { CloneMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloneMenuButton/CloneMenuButton';
import { CloseMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloseMenuButton/CloseMenuButton';
import { DownloadMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/DownloadMenuButton/DownloadMenuButton';
import { EditMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/EditMenuButton/EditMenuButton';
import { FacesMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/FacesMenuButton/FacesMenuButton';
import { MenuInfo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuInfo/MenuInfo';
import { MoreMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton';
import { MenuItemName } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton.types';
import { ShareMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/ShareMenuButton/ShareMenuButton';
import {
    type ViewerMenuProps,
    ViewerMenuCenter,
    ViewerMenuEnd,
    ViewerMenuStart,
    ViewerMenuWrapper,
} from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu';
import { Zoomer } from 'reactApp/ui/ReactViewer/ViewerMenu/Zoomer/Zoomer';
import { RegisterCloudTooltip } from 'reactApp/ui/RegisterCloudTooltip/RegisterCloudTooltip';
import { DownloadLimitsPromo } from 'reactApp/ui/SharingTooltips/DownloadLimitsPromo';
import { canCloneWeblink } from 'reactApp/utils/cloneHelpers';
import { sendDwh } from 'reactApp/utils/ga';

import { MenuLogo } from '../MenuLogo/MenuLogo';

interface Props extends ViewerMenuProps {
    file: CloudFile | AttachesItem;
}

export const PublicMenu: FC<Props> = ({
    file,
    fileStorage,
    showLogo,
    faces = 0,
    embedded,
    isAuthorized,
    isArchiveItem,
    isAttachesEnabled,
    parent,
    zoom,
    decreaseZoom,
    increaseZoom,
    onShowFaceFilter,
    onPublish,
    onClone,
    showDownload,
    onDownload,
    showImageEdit,
    onImageEdit,
    isPublicCopyEditable,
    onPublicCopyEdit,
    onOpenInMyCloud,
    onMail,
    onComplain,
    showClose,
    onClose,
    isEditingDisabled,
}) => {
    const isTempAttachType = 'attachType' in file && file.attachType === EAttachTypes.temporary;
    const showCloneWeblink = canCloneWeblink(isAuthorized, fileStorage, true);
    const showClone = !file.weblinkDownloadable && showCloneWeblink && !isTempAttachType;
    const isNotAuthTarm = IS_TARM && !isAuthorized;
    const showOpenInCloud = !showClone && !file.weblinkDownloadable && embedded && getIsCloudAvailabe();
    const showMail = isAttachesEnabled && !embedded && !isArchiveItem;
    const showComplain = seoEnable && embedded;

    const isEditable = 'weblinkAccessRights' in file && file.weblinkAccessRights === 'rw';
    const showPublicCopyEdit =
        (isEditPublicCopy || isEditable) &&
        isPublicCopyEditable &&
        showCloneWeblink &&
        !IS_BIZ_USER &&
        !(IS_TABLET_BROWSER || browser.isIpadOs());

    const shareRef = useRef<HTMLDivElement>(null);
    const downloadRef = useRef<HTMLDivElement>(null);

    const moreItems = useMemo(
        () => [
            ...(showOpenInCloud ? [{ name: MenuItemName.OPEN_IN_MY, divider: true, onClick: onOpenInMyCloud }] : []),
            ...(showMail ? [{ name: MenuItemName.MAIL, divider: true, onClick: onMail }] : []),
            ...(showComplain
                ? [
                      {
                          name: MenuItemName.COMPLAIN,
                          onClick: onComplain,
                      },
                  ]
                : []),
        ],
        [showOpenInCloud, onOpenInMyCloud, showMail, onMail, showComplain, onComplain]
    );

    const [showDownloadPromo, setShowDownloadPromo] = useState(false);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const currentItem = useSelector((state: RootState) => getCurrentItem(state));
    const routeId = useSelector(getCurrentRouteId);
    const weblink = getWeblinkFromPublicId(routeId);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblink || ''));
    const showDownloadLimitsPromo = tooltipLimitPublic && !IS_BIZ_USER && !IS_CORP_USER && !isPaidUser && downloadLimits?.left === 0;

    useEffect(() => {
        if (showDownloadLimitsPromo) {
            sendDwh({ eventCategory: 'viewer', action: 'no_download_show' });
        }
    }, []);

    const onCustomClick = (e) => {
        e.preventDefault();
        sendDwh({ eventCategory: 'viewer', action: 'no_download_click' });
        setShowDownloadPromo(true);
    };
    const handleClickClose = () => {
        setShowDownloadPromo(false);
    };

    return (
        <ViewerMenuWrapper showDownload={showDownload}>
            <ViewerMenuStart>
                {((showLogo && IS_B2B_BIZ_USER) || !!parent) && <MenuLogo />}
                <MenuInfo file={file} />
            </ViewerMenuStart>
            <ViewerMenuCenter>
                {zoom && <Zoomer value={zoom} decrease={decreaseZoom} increase={increaseZoom} />}
                {faces ? <FacesMenuButton count={faces} onClick={onShowFaceFilter} /> : null}
            </ViewerMenuCenter>
            <ViewerMenuEnd>
                {!isArchiveItem && embedded && !file.weblinkDownloadable && (
                    <>
                        <ShareMenuButton
                            ref={shareRef}
                            text={showCloneWeblink ? 'Получить ссылку' : 'Отключить ссылку'}
                            onClick={() => {
                                onPublish(file);
                            }}
                        />
                        {fileStorage !== EStorageType.stock && <RegisterCloudTooltip targetRef={shareRef} closeOnScroll={true} />}
                    </>
                )}
                {showImageEdit && (
                    <EditMenuButton
                        onClick={() => {
                            onImageEdit(file);
                        }}
                    />
                )}
                {showPublicCopyEdit && (
                    <EditorRestrictionTooltipWrapper isEditingDisabled={isEditingDisabled}>
                        <EditMenuButton
                            hint={isEditable ? 'Редактировать' : 'Редактировать копию'}
                            qaId="edit_public_copy"
                            onClick={() => {
                                if (isEditingDisabled) {
                                    return;
                                }
                                onPublicCopyEdit(file);
                            }}
                        />
                    </EditorRestrictionTooltipWrapper>
                )}
                {!isArchiveItem && showClone && !isNotAuthTarm && <CloneMenuButton onClick={onClone} />}
                {showDownload && (
                    <div ref={downloadRef}>
                        <DownloadMenuButton
                            file={file}
                            onClick={
                                showDownloadLimitsPromo
                                    ? () => {
                                          onCustomClick(event);
                                      }
                                    : () => {
                                          onDownload(file);
                                      }
                            }
                            disabled={showDownloadLimitsPromo}
                        />
                    </div>
                )}
                {showDownload && showDownloadPromo && (
                    <DownloadLimitsPromo targetRef={downloadRef} onClose={handleClickClose} isDarkMode item={currentItem} />
                )}
                <MoreMenuButton list={moreItems} />
                {showClose && (embedded || !!parent) && (
                    <CloseMenuButton
                        onClick={() => {
                            onClose(file);
                        }}
                    />
                )}
            </ViewerMenuEnd>
        </ViewerMenuWrapper>
    );
};
